import React from 'react';

import { Alert, AlertTitle, Button } from '@mui/material';

import { HeroPlannerDebugPreSendCheck } from '~/hooks/apis/customer-communication/useHeroPlannerScheduleDebug';

import {
  getHeroPlannerDebugPreSendCheckName,
  isHeroPlannerDebugPreSendCheckPassing,
} from '../../utils/heroPlannerDebugUtils';

import HeroPlannerDebugErrorList from './HeroPlannerDebugErrorList';

interface Props {
  check: HeroPlannerDebugPreSendCheck;
}

/** What the backend's pre-send check found for a country group (or one of its segments); nothing when it passes */
function HeroPlannerDebugPreSendCheckResult({ check }: Props) {
  const { query } = check;
  const name = getHeroPlannerDebugPreSendCheckName(check.segment);

  if (query.isError) {
    return (
      <Alert
        severity="warning"
        action={
          <Button size="small" color="inherit" onClick={() => query.refetch()}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Couldn't run the {name.toLowerCase()}</AlertTitle>
        {query.error instanceof Error ? query.error.message : String(query.error)}
      </Alert>
    );
  }

  if (!query.data) {
    return null;
  }

  const eveningPushErrors = query.data.eveningPushErrors ?? [];
  const isPassing = isHeroPlannerDebugPreSendCheckPassing(query.data);

  return (
    <>
      {!isPassing && (
        <Alert severity="error">
          <AlertTitle>
            {name} failing - the send won't go out until {query.data.errors.length ? 'these are fixed' : 'this passes'}
          </AlertTitle>
          {query.data.errors.length > 0 ? (
            <HeroPlannerDebugErrorList errors={query.data.errors} />
          ) : (
            `The check returned ${query.data.status} without any details.`
          )}
        </Alert>
      )}
      {eveningPushErrors.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>Evening push issues (these don't block the send)</AlertTitle>
          <HeroPlannerDebugErrorList errors={eveningPushErrors} />
        </Alert>
      )}
    </>
  );
}

export default HeroPlannerDebugPreSendCheckResult;
